import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { DAY_APPLY_SCOPE_OPTIONS, DAY_APPLY_SCOPES, countDayApplyScopeDates } from '../utils/calendarHelpers';
import { GlossySaveButton } from './calendar/GlossySaveButton';

/**
 * Choose which show dates receive the current tab edit when saved.
 */
export function DayApplyScopePicker({
  value,
  onChange,
  anchorDateKey,
  tourShowDateKeys = [],
  theme,
  compact = false,
  contained = false,
  showSaveButton = false,
  onSave,
  saving = false,
  saveDisabled = false,
}) {
  const dayCount = countDayApplyScopeDates(value, anchorDateKey, tourShowDateKeys);
  const styles = useStyles(theme, compact);

  return (
    <View style={[styles.wrap, contained && styles.wrapContained]}>
      <Text style={styles.label}>Copy to</Text>
      <View style={styles.options}>
        {DAY_APPLY_SCOPE_OPTIONS.map((opt) => {
          const selected = value === opt.id;
          const count = countDayApplyScopeDates(opt.id, anchorDateKey, tourShowDateKeys);
          return (
            <View key={opt.id} style={styles.optionCell}>
              <TouchableOpacity
                style={[styles.option, selected && styles.optionSelected]}
                onPress={() => onChange(opt.id)}
                accessibilityRole="radio"
                accessibilityState={{ selected }}
                accessibilityLabel={`${opt.label}, ${count} day${count === 1 ? '' : 's'}`}
              >
                <Text style={[styles.optionText, selected && styles.optionTextSelected]} numberOfLines={2}>
                  {opt.label}
                </Text>
                {count > 1 ? (
                  <Text style={[styles.optionCount, selected && styles.optionCountSelected]}>
                    {count} show days
                  </Text>
                ) : null}
              </TouchableOpacity>
            </View>
          );
        })}
      </View>
      {value !== DAY_APPLY_SCOPES.THIS_DAY && dayCount > 1 ? (
        <Text style={styles.hint}>
          {showSaveButton
            ? `Tap Save below to copy this tab to ${dayCount} show days on this tour.`
            : `Saving will copy this tab's content to ${dayCount} show days on this tour.`}
        </Text>
      ) : null}
      {showSaveButton ? (
        <GlossySaveButton
          onPress={onSave}
          disabled={saveDisabled || saving}
          theme={theme}
          style={(saveDisabled || saving) && styles.saveBtnDisabled}
        >
          {saving
            ? 'Saving…'
            : dayCount > 1
              ? `Save to ${dayCount} show days`
              : 'Save changes'}
        </GlossySaveButton>
      ) : null}
    </View>
  );
}

function useStyles(theme, compact) {
  return StyleSheet.create({
    wrap: {
      gap: compact ? 8 : 10,
      width: '100%',
      minWidth: 0,
    },
    wrapContained: {
      paddingTop: 2,
    },
    label: {
      fontSize: compact ? 11 : 12,
      fontWeight: '800',
      color: theme.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      textAlign: 'left',
    },
    options: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      width: '100%',
      marginHorizontal: -3,
    },
    optionCell: {
      width: '50%',
      paddingHorizontal: 3,
      paddingBottom: 6,
      minWidth: 0,
    },
    option: {
      width: '100%',
      minHeight: compact ? 52 : 56,
      paddingVertical: compact ? 10 : 12,
      paddingHorizontal: 8,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 0,
    },
    optionSelected: {
      borderColor: theme.brand.primary,
      backgroundColor: `${theme.brand.primary}18`,
    },
    optionText: {
      fontSize: compact ? 12 : 13,
      fontWeight: '600',
      color: theme.text.primary,
      textAlign: 'center',
    },
    optionTextSelected: {
      color: theme.brand.primary,
      fontWeight: '800',
    },
    optionCount: {
      marginTop: 4,
      fontSize: 11,
      color: theme.text.secondary,
      textAlign: 'center',
    },
    optionCountSelected: {
      color: theme.brand.primary,
    },
    hint: {
      fontSize: 11,
      color: theme.text.secondary,
      lineHeight: 15,
      textAlign: 'left',
    },
    saveBtnDisabled: {
      opacity: 0.55,
    },
  });
}

export default DayApplyScopePicker;
